import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useCompany } from "../../components/context/CompanyContext";
import { userRequest } from "../../requestMethods";
import "../../global.css";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// ── Poses por gênero ─────────────────────────────────────────────────
const POSE_LABELS = {
  female: [
    { id: 0, label: "Frontal", desc: "De frente para a câmera" },
    { id: 1, label: "3/4 Esquerda", desc: "Levemente virada" },
    { id: 2, label: "Mão no Quadril", desc: "Pose confiante" },
    { id: 3, label: "Casual", desc: "Descontraída" },
  ],
  male: [
    { id: 0, label: "Frontal", desc: "De frente para a câmera" },
    { id: 1, label: "3/4 Esquerda", desc: "Levemente virado" },
    { id: 2, label: "Braços Cruzados", desc: "Pose confiante" },
    { id: 3, label: "Casual", desc: "Mãos nos bolsos" },
  ],
};

// ── Opções ──────────────────────────────────────────────────────────
const MODEL_STYLE_OPTIONS = [
  { id: "female", label: "Feminino", emoji: "👩" },
  { id: "male", label: "Masculino", emoji: "👨" },
];

const ETHNICITY_OPTIONS = [
  { id: "white", label: "Branca" },
  { id: "black", label: "Negra" },
  { id: "mixed", label: "Parda" },
  { id: "asian", label: "Asiática" },
  { id: "latina", label: "Latina" },
];

const BODY_TYPE_OPTIONS = [
  { id: "slim", label: "Magra" },
  { id: "medium", label: "Média" },
  { id: "plus_size", label: "Plus Size" },
];

const AGE_OPTIONS = [
  { id: "young", label: "Jovem", sub: "20–25" },
  { id: "adult", label: "Adulta", sub: "30–35" },
  { id: "mature", label: "Madura", sub: "45–50" },
];

const BACKGROUND_OPTIONS = [
  { id: "studio_white", label: "Estúdio Branco" },
  { id: "studio_gray", label: "Estúdio Cinza" },
  { id: "urban", label: "Urbano" },
  { id: "nature", label: "Natureza" },
  { id: "elegant_interior", label: "Interior" },
];

// ── Componente de chip selecionável ─────────────────────────────────
function OptionChip({ label, sub, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        padding: sub ? "6px 10px" : "7px 14px",
        borderRadius: 8,
        border: selected ? "2px solid #ee8253" : "2px solid #e0e0e0",
        background: selected ? "#fff5f1" : "#fff",
        color: selected ? "#c05a30" : "#555",
        fontWeight: selected ? 700 : 400,
        fontSize: 13,
        textAlign: "center",
        transition: "all 0.15s",
        lineHeight: 1.3,
        userSelect: "none",
      }}
    >
      {label}
      {sub && (
        <div
          style={{
            fontSize: 10,
            color: selected ? "#ee8253" : "#aaa",
            marginTop: 1,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

// ── Cabeçalho de seção com número ───────────────────────────────────
function StepHeader({ number, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#ee8253",
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {number}
      </div>
      <span style={{ fontWeight: 600, fontSize: 14, color: "#222" }}>
        {title}
      </span>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────
export default function ShowcasePage() {
  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId } = useCompany();
  const nav = useNavigate();
  const fileInputRef = useRef(null);

  const [uploadedImage, setUploadedImage] = useState(null);
  const [modelStyle, setModelStyle] = useState("female");
  const [ethnicity, setEthnicity] = useState("white");
  const [bodyType, setBodyType] = useState("medium");
  const [age, setAge] = useState("young");
  const [background, setBackground] = useState("studio_white");
  const [selectedPoses, setSelectedPoses] = useState([0]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const togglePose = (id) => {
    setSelectedPoses((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev; // mínimo 1
        return prev.filter((p) => p !== id);
      }
      return [...prev, id].sort((a, b) => a - b);
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire("Atenção", "Selecione um arquivo de imagem válido.", "warning");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setUploadedImage({ preview: dataUrl, base64: dataUrl.split(",")[1] });
      setGeneratedImages([]);
      setSelectedIndex(0);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange({ target: { files: [file] } });
  };

  const handleGenerate = async () => {
    if (!uploadedImage) {
      Swal.fire(
        "Atenção",
        "Faça o upload de uma imagem da roupa antes de gerar.",
        "warning",
      );
      return;
    }
    setIsGenerating(true);
    setGeneratedImages([]);
    setSelectedIndex(0);
    try {
      const res = await userRequest.post(`${companyId}/virtual/showcase`, {
        productImage: uploadedImage.base64,
        modelStyle,
        ethnicity,
        bodyType,
        age,
        background,
        selectedPoses,
        userId: currentUser?.userId || null,
      });
      if (res.data.success && res.data.images?.length > 0) {
        setGeneratedImages(res.data.images);
      } else {
        Swal.fire("Erro", res.data.error || "Erro ao gerar imagem.", "error");
      }
    } catch (error) {
      Swal.fire(
        "Erro",
        error.response?.data?.error ||
          "Erro ao gerar vitrine. Tente novamente.",
        "error",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    const img = generatedImages[selectedIndex];
    if (!img) return;
    const link = document.createElement("a");
    link.href = img.dataUrl;
    link.download = `vitrine_ai_${Date.now()}.png`;
    link.click();
  };

  const selectedImage = generatedImages[selectedIndex] || null;

  return (
    <div className="div_custom_">
      <div className="div_custom_Container">
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 24,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => nav(-1)}
            size="small"
            variant="text"
            style={{ color: "#ee8253" }}
          >
            Voltar
          </Button>
          <AutoAwesomeIcon style={{ color: "#ee8253" }} />
          <span style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
            Smart Look IA
          </span>
          <span style={{ fontSize: 12, color: "#aaa", marginLeft: 4 }}>
            Gere fotos profissionais de modelos vestindo sua roupa
          </span>
        </div>

        <div
          style={{
            display: "flex",
            gap: 28,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {/* ── Painel esquerdo: configurações ── */}
          <div
            style={{
              flex: "0 0 300px",
              background: "#fff",
              borderRadius: 16,
              border: "1px solid var(--opacity-8, rgba(0,0,0,0.08))",
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 20,
              boxShadow: "0px 4px 8px 0px rgba(0,0,0,0.08)",
            }}
          >
            {/* 1. Upload */}
            <div>
              <StepHeader number="1" title="Imagem da Roupa" />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                style={{
                  width: "100%",
                  minHeight: 140,
                  border: `2px dashed ${uploadedImage ? "#ee8253" : "#ddd"}`,
                  borderRadius: 12,
                  background: uploadedImage ? "#fff8f5" : "#fafafa",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                }}
              >
                {uploadedImage ? (
                  <img
                    src={uploadedImage.preview}
                    alt="Roupa"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      padding: 8,
                    }}
                  />
                ) : (
                  <>
                    <CloudUploadIcon
                      style={{ fontSize: 36, color: "#ccc", marginBottom: 6 }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        color: "#888",
                        textAlign: "center",
                        padding: "0 12px",
                      }}
                    >
                      Clique ou arraste a imagem
                    </span>
                    <span style={{ fontSize: 11, color: "#bbb", marginTop: 3 }}>
                      JPG · PNG · WEBP
                    </span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {uploadedImage && (
                <button
                  onClick={() => {
                    setUploadedImage(null);
                    setGeneratedImages([]);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  style={{
                    marginTop: 6,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 11,
                    color: "#bbb",
                  }}
                >
                  Remover imagem
                </button>
              )}
            </div>

            <div style={{ height: 1, background: "#f0f0f0" }} />

            {/* 2. Gênero */}
            <div>
              <StepHeader number="2" title="Gênero" />
              <div style={{ display: "flex", gap: 8 }}>
                {MODEL_STYLE_OPTIONS.map((opt) => (
                  <div key={opt.id} style={{ flex: 1 }}>
                    <OptionChip
                      label={`${opt.emoji} ${opt.label}`}
                      selected={modelStyle === opt.id}
                      onClick={() => setModelStyle(opt.id)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 3. Raça */}
            <div>
              <StepHeader number="3" title="Raça" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ETHNICITY_OPTIONS.map((opt) => (
                  <OptionChip
                    key={opt.id}
                    label={opt.label}
                    selected={ethnicity === opt.id}
                    onClick={() => setEthnicity(opt.id)}
                  />
                ))}
              </div>
            </div>

            {/* 4. Tipo de Corpo */}
            <div>
              <StepHeader number="4" title="Tipo de Corpo" />
              <div style={{ display: "flex", gap: 6 }}>
                {BODY_TYPE_OPTIONS.map((opt) => (
                  <div key={opt.id} style={{ flex: 1 }}>
                    <OptionChip
                      label={opt.label}
                      selected={bodyType === opt.id}
                      onClick={() => setBodyType(opt.id)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 5. Idade */}
            <div>
              <StepHeader number="5" title="Idade" />
              <div style={{ display: "flex", gap: 6 }}>
                {AGE_OPTIONS.map((opt) => (
                  <div key={opt.id} style={{ flex: 1 }}>
                    <OptionChip
                      label={opt.label}
                      sub={opt.sub}
                      selected={age === opt.id}
                      onClick={() => setAge(opt.id)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 6. Fundo */}
            <div>
              <StepHeader number="6" title="Fundo" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {BACKGROUND_OPTIONS.map((opt) => (
                  <OptionChip
                    key={opt.id}
                    label={opt.label}
                    selected={background === opt.id}
                    onClick={() => setBackground(opt.id)}
                  />
                ))}
              </div>
            </div>

            {/* 7. Poses */}
            <div>
              <StepHeader number="7" title="Poses" />
              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 8 }}>
                {selectedPoses.length} de 4 selecionadas — mínimo 1
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                }}
              >
                {POSE_LABELS[modelStyle].map((pose) => (
                  <div
                    key={pose.id}
                    onClick={() => togglePose(pose.id)}
                    style={{
                      cursor: "pointer",
                      padding: "7px 10px",
                      borderRadius: 8,
                      border: selectedPoses.includes(pose.id)
                        ? "2px solid #ee8253"
                        : "2px solid #e0e0e0",
                      background: selectedPoses.includes(pose.id)
                        ? "#fff5f1"
                        : "#fff",
                      color: selectedPoses.includes(pose.id)
                        ? "#c05a30"
                        : "#555",
                      fontWeight: selectedPoses.includes(pose.id) ? 700 : 400,
                      fontSize: 12,
                      transition: "all 0.15s",
                      userSelect: "none",
                      lineHeight: 1.4,
                    }}
                  >
                    <div>{pose.label}</div>
                    <div
                      style={{
                        fontSize: 10,
                        color: selectedPoses.includes(pose.id)
                          ? "#ee8253"
                          : "#bbb",
                        marginTop: 1,
                      }}
                    >
                      {pose.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: "#f0f0f0" }} />

            {/* Botão Gerar */}
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isGenerating || !uploadedImage}
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              style={{
                background: uploadedImage ? "#ee8253" : "#ccc",
                color: "#fff",
                fontWeight: 700,
                borderRadius: 10,
                padding: "11px 0",
                fontSize: 15,
                textTransform: "none",
                boxShadow: "none",
              }}
              fullWidth
            >
              {isGenerating
                ? "Gerando fotos..."
                : `Gerar ${selectedPoses.length} ${selectedPoses.length === 1 ? "Foto" : "Fotos"}`}
            </Button>

            {isGenerating && (
              <div
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  color: "#aaa",
                  lineHeight: 1.9,
                }}
              >
                Gerando modelos e aplicando a roupa...
                <br />
                <span style={{ color: "#ee8253" }}>
                  Pode levar até 2 minutos
                </span>
              </div>
            )}
          </div>

          {/* ── Painel direito: resultado ── */}
          <div style={{ flex: 1, minWidth: 300 }}>
            {isGenerating ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={460}
                  style={{ borderRadius: 16 }}
                  animation="wave"
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${selectedPoses.length}, 1fr)`,
                    gap: 8,
                  }}
                >
                  {selectedPoses.map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={90}
                      style={{ borderRadius: 10 }}
                      animation="wave"
                    />
                  ))}
                </div>
              </div>
            ) : generatedImages.length > 0 ? (
              <div>
                {/* Imagem principal */}
                <div
                  style={{
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#f9f9f9",
                    border: "1px solid var(--opacity-8, rgba(0,0,0,0.08))",
                    boxShadow: "0px 4px 8px 0px rgba(0,0,0,0.08)",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 380,
                  }}
                >
                  <img
                    src={selectedImage?.dataUrl}
                    alt="Vitrine gerada"
                    style={{
                      width: "100%",
                      maxHeight: 520,
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Miniaturas */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${generatedImages.length}, 1fr)`,
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  {generatedImages.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => setSelectedIndex(i)}
                      style={{
                        cursor: "pointer",
                        borderRadius: 10,
                        overflow: "hidden",
                        border:
                          i === selectedIndex
                            ? "3px solid #ee8253"
                            : "3px solid transparent",
                        background: "#f5f5f5",
                        transition: "border-color 0.15s",
                      }}
                    >
                      <img
                        src={img.dataUrl}
                        alt={`Foto ${i + 1}`}
                        style={{
                          width: "100%",
                          height: 90,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Ações */}
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    style={{
                      flex: 1,
                      background: "#ee8253",
                      borderRadius: 10,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "none",
                    }}
                  >
                    Baixar Selecionada
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    style={{
                      flex: 1,
                      borderRadius: 10,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#ee8253",
                      color: "#ee8253",
                    }}
                  >
                    Gerar Novamente
                  </Button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  minHeight: 460,
                  background: "#fafafa",
                  borderRadius: 16,
                  border: "2px dashed #e0e0e0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#bbb",
                  gap: 10,
                }}
              >
                <AutoAwesomeIcon
                  style={{ fontSize: 52, color: "#ee8253", opacity: 0.3 }}
                />
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "center",
                    padding: "0 32px",
                    lineHeight: 1.6,
                  }}
                >
                  Configure as opções ao lado e clique em
                  <br />
                  <strong style={{ color: "#ee8253" }}>Gerar Fotos</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
